/// <amd-module name="WidgetCommon/WitUtils/LocalStorage/LocalStorageWrapper" />
/**
 * Scope at which the local user setting applies
 */
export enum LocalStorageScope {
    /**
     * Global (account-specific) settings for a user
     */
    Global,

    /**
     * Project-specific settings for a user
     */
    Project,

    /**
     * Team-specific settings for a user
     */
    Team
}

export interface TeamContext {
    projectId: string;
    teamId: string;
}

/**
 * Service for reading and writing to local storage
 */
export class LocalStorageWrapper {
    private static GLOBAL_SETTING_KEY = "global";
    private static PROJECT_SETTING_KEY = "project";
    private static TEAM_SETTING_KEY = "team";

    constructor(private context: TeamContext) {}

    /**
     * Write a settings value to browser local storage
     *
     * @param key Key for the setting to be written. This key will be prefixed with a scope.
     * @param value Value for the setting to be written
     * @param scope Scope for the setting to apply to. This will determine the prefix to use at the beginning of the setting key.
     */
    public write(key: string, value: any, scope: LocalStorageScope = LocalStorageScope.Global) {
        const scopedKey = this._getScopedKey(key, scope);
        if (scopedKey) {
            try {
                window.localStorage.setItem(scopedKey, JSON.stringify({ v: value }));
            } catch (error) {
                console.error("Failed to write to local storage: " + error);
            }
        }
    }

    /**
     * Read a setting from browser local storage.
     *
     * @param key Key for the setting to be written. This key will be prefixed with a scope.
     * @param defaultValue The value to return in case no setting exists
     * @param scope Scope for the setting to apply to. This will determine the prefix to use at the beginning of the setting key.
     * @return Value read from the setting or undefined if no value stored
     */
    public read<T>(key: string, defaultValue: T | undefined = undefined, scope: LocalStorageScope = LocalStorageScope.Global): T | undefined {
        const scopedKey = this._getScopedKey(key, scope);
        if (scopedKey) {
            try {
                const value = window.localStorage.getItem(scopedKey);
                if (value && typeof value === "string") {
                    return JSON.parse(value).v as T;
                }
            } catch (error) {}
        }
        return defaultValue;
    }

    private _getScopedKey(key: string, scope: LocalStorageScope): string | undefined {
        switch (scope) {
            case LocalStorageScope.Global:
                return LocalStorageWrapper.GLOBAL_SETTING_KEY + "/" + key;

            case LocalStorageScope.Project:
                if (!this.context.projectId) {
                    return undefined;
                }
                return LocalStorageWrapper.PROJECT_SETTING_KEY + "/" + this.context.projectId + "/" + key;

            case LocalStorageScope.Team:
                if (!this.context.teamId) {
                    return undefined;
                }
                return LocalStorageWrapper.TEAM_SETTING_KEY + "/" + this.context.teamId + "/" + key;

            default:
                return undefined;
        }
    }
}
